import { GetServerSidePropsContext } from 'next';
import { useEffect } from 'react';
import { useDispatch } from 'react-redux';

import SEO from '@components/common/SEO/SEO';
import { PlatformType } from '@invideoio/web-shared/constants/common';
import LandingPageContainer from '@layouts/LandingPageLayout/LandingPageContainer';
import ToolLandingLayout from '@layouts/LandingPageLayout/ToolLandingLayout';
import { getCompressPage } from '@lib/API';
import { SET_OS, SET_PLATFORM } from '@store/device-state';
import { useAppSelector } from '@store/hook';
import { getUploadFileDetails } from '@store/upload-state/upload-state';
import { userStateSelector } from '@store/user-state/UserState';
import { filterBlogResponseForCards } from '@utils/commonUtils';
import { generateJSONLDSchema } from '@utils/seo';
import { RootProps } from 'src/pages/_app';
import { ToolsLandingPageResponse } from '../../../types/landing-page';

interface CompressProps extends RootProps {
  compressPageState: ToolsLandingPageResponse;
}

function CompressLandingPage(props: CompressProps) {
  const {
    compressPageState: toolsPageState,
    os,
    socialLinks,
    platform,
    browser,
  } = props;

  const { FAQItems, SEOSection } = toolsPageState;

  const dispatch = useDispatch();

  const user = useAppSelector(userStateSelector);
  const uploadState = useAppSelector(getUploadFileDetails);

  const LDschema = generateJSONLDSchema(
    {
      Title: SEOSection.Title,
      Description: SEOSection.Description,
      faqItems: FAQItems,
    },
    SEOSection.CanonicalURL,
    'WebPage',
  );

  useEffect(() => {
    if (navigator.platform === 'MacIntel' && navigator.maxTouchPoints > 1) {
      dispatch(SET_PLATFORM(PlatformType.Tablet));
    } else {
      dispatch(SET_PLATFORM(platform));
    }
    dispatch(SET_OS(os));

    // Cancel the upload if user navigates backs to LP
    if (uploadState.cancelToken) {
      uploadState.cancelToken('Cancelled Previous upload');
    }
  }, []);

  return (
    <ToolLandingLayout>
      <SEO
        title={SEOSection.Title}
        description={SEOSection.Description}
        canonicalurl={SEOSection.CanonicalURL}
        keywords={SEOSection.Keywords}
        robotIndex={SEOSection.RobotIndex}
        robotFollow={SEOSection.RobotFollow}
      >
        <script
          type="application/ld+json"
          dangerouslySetInnerHTML={{
            __html: `${JSON.stringify(LDschema)}`,
          }}
        />
      </SEO>
      <LandingPageContainer
        landingpageState={toolsPageState}
        user={user}
        socialLinks={socialLinks}
        allowEditor={true}
        platform={platform}
        browser={browser}
      />
    </ToolLandingLayout>
  );
}

export async function getServerSideProps(ctx: GetServerSidePropsContext) {
  let compressPageState = null;
  const slug = ctx.query.id as string;
  const lang = ctx.locale;

  try {
    compressPageState = await getCompressPage(slug, lang);

    if (compressPageState) {
      if (Array.isArray(compressPageState.invideo_blogs)) {
        compressPageState.invideo_blogs = filterBlogResponseForCards(
          compressPageState.invideo_blogs,
        );
      }
      return {
        props: {
          compressPageState,
        },
      };
    }

    return {
      notFound: true,
    };
  } catch (error) {
    console.error(error);
    return {
      notFound: true,
    };
  }
}

export default CompressLandingPage;
